import { UiIconButtonToggleComponent } from '@/ui/icon-button-toggle/ui-icon-button-toggle';
import { IconName } from '@/ui/icon/ui-icon';
import { UiSectionActionsDirective } from '@/ui/section/section-actions/ui-section-actions';
import { UiSectionContentComponent } from '@/ui/section/section-content/ui-section-content';
import { UiSectionComponent } from '@/ui/section/ui-section';
import { isNullish } from '@/utils/typing/is-nullish.predicate';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    inputBinding,
    type OnInit,
    output,
    outputBinding,
    signal,
    viewChild,
    ViewContainerRef
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import type { Observable } from 'rxjs';
import type {
    BusinessIntelligenceDimension,
    BusinessIntelligenceMeasure,
    BusinessIntelligenceRepresentation
} from '../../../interfaces/BusinessIntelligence.interface';
import {
    type AnyBusinessIntelligenceRepresentationConfig,
    BusinessIntelligenceRepresentationCode
} from '../../../interfaces/BusinessIntelligenceRepresentation.interface';
import { BusinessIntelligenceRepresentationsProvider } from '../../../providers/business-intelligence/business-intelligence-representations.provider';

const DEFAULT_REPRESENTATION_CODE = BusinessIntelligenceRepresentationCode.HISTOGRAM;

export interface RepresentationWidgetConfigEvent {
    biRepresentation: BusinessIntelligenceRepresentation;
    isValid: boolean;
}

@UntilDestroy()
@Component({
    // The dynamically spawned config editors are legacy `Eager` components that rely on being
    // traversed by change detection, so this host cannot be `OnPush`.
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./business-intelligence-widget-representation-config-editor.scss'],
    selector: 'business-intelligence-widget-representation-config-editor',
    templateUrl: 'business-intelligence-widget-representation-config-editor.html',
    imports: [
        UiSectionComponent,
        UiSectionActionsDirective,
        UiIconButtonToggleComponent,
        FormsModule,
        UiSectionContentComponent,
        TranslatePipe
    ]
})
export class BusinessIntelligenceWidgetRepresentationConfigEditorComponent implements OnInit {
    private _businessIntelligenceRepresentationsProvider = inject(BusinessIntelligenceRepresentationsProvider);

    representationWidgetConfig = input<BusinessIntelligenceRepresentation | null>();

    availableMeasures = input<BusinessIntelligenceMeasure[], BusinessIntelligenceMeasure[] | null | undefined>([], {
        transform: measures => measures ?? []
    });

    availableDimensions = input<BusinessIntelligenceDimension[], BusinessIntelligenceDimension[] | null | undefined>([], {
        transform: dimensions => dimensions ?? []
    });

    disabled = input(false);
    resetNeeded$ = input<Observable<void>>();

    configChanged = output<RepresentationWidgetConfigEvent>();

    private _formHost = viewChild.required('formHost', { read: ViewContainerRef });

    representationSelected = signal<BusinessIntelligenceRepresentationCode>(DEFAULT_REPRESENTATION_CODE);

    /** Seeded with the default representation config, then resolved from the bound configuration in `ngOnInit`. */
    configToEdit = signal<AnyBusinessIntelligenceRepresentationConfig>(
        this._businessIntelligenceRepresentationsProvider.getDefaultRepresentationConfig(DEFAULT_REPRESENTATION_CODE)
    );

    private _representationFormComponent = computed(() =>
        this._businessIntelligenceRepresentationsProvider.getRepresentationConfigEditorComponent(this.representationSelected())
    );

    readonly representationList: { code: BusinessIntelligenceRepresentationCode; iconName: IconName }[] = [
        {
            code: BusinessIntelligenceRepresentationCode.LINE_CHART,
            iconName: IconName.COMBO_CHART
        },
        {
            code: BusinessIntelligenceRepresentationCode.PIE_CHART,
            iconName: IconName.PIE_CHART
        },
        {
            code: BusinessIntelligenceRepresentationCode.BAR_CHART,
            iconName: IconName.BAR_CHART
        },
        {
            code: BusinessIntelligenceRepresentationCode.SCATTER_CHART,
            iconName: IconName.SCATTER_CHART
        },
        {
            code: BusinessIntelligenceRepresentationCode.TABLE,
            iconName: IconName.TABLE
        },
        {
            code: BusinessIntelligenceRepresentationCode.PIVOT_TABLE,
            iconName: IconName.PIVOT_TABLE
        },
        {
            code: BusinessIntelligenceRepresentationCode.SINGLE_VALUE,
            iconName: IconName.SINGLE_VALUE
        },
        {
            code: BusinessIntelligenceRepresentationCode.HISTOGRAM,
            iconName: IconName.COLUMN_CHART
        }
    ];

    private _lastUserUpdatedState!: {
        representationCode: BusinessIntelligenceRepresentationCode;
        representationConfig: AnyBusinessIntelligenceRepresentationConfig;
    };

    constructor() {
        // Only a representation change recreates the form: the config, the available members and the
        // disabled state reach the mounted form through their reactive bindings.
        effect(onCleanup => {
            const representationFormComponent = this._representationFormComponent();
            if (isNullish(representationFormComponent)) {
                return;
            }
            // The output subscription declared via `outputBinding` is torn down on `ref.destroy()`.
            const ref = this._formHost().createComponent(representationFormComponent, {
                bindings: [
                    inputBinding('config', this.configToEdit),
                    inputBinding('availableMeasures', this.availableMeasures),
                    inputBinding('availableDimensions', this.availableDimensions),
                    inputBinding('disabled', this.disabled),
                    outputBinding<AnyBusinessIntelligenceRepresentationConfig>('configChanged', config => this.onConfigChange(config))
                ]
            });
            onCleanup(() => ref.destroy());
        });
        effect(onCleanup => {
            const resetNeeded$ = this.resetNeeded$();
            if (isNullish(resetNeeded$)) {
                return;
            }
            const subscription = resetNeeded$.subscribe(() => {
                this.resetConfig(this.representationSelected());
            });
            onCleanup(() => subscription.unsubscribe());
        });
    }

    ngOnInit() {
        const representationWidgetConfig = this.representationWidgetConfig();

        if (representationWidgetConfig) {
            this.representationSelected.set(representationWidgetConfig.representationCode);
            // A persisted widget can carry a representation without its config: fall back on the default.
            this.configToEdit.set(
                representationWidgetConfig.representationConfig ??
                    this._businessIntelligenceRepresentationsProvider.getDefaultRepresentationConfig(this.representationSelected())
            );
        }
        this.updateLastUserUpdatedState();
    }

    changeRepresentation(newRepresentationCode: BusinessIntelligenceRepresentationCode) {
        if (newRepresentationCode === this._lastUserUpdatedState.representationCode) {
            this.configToEdit.set(this._lastUserUpdatedState.representationConfig);
        } else {
            this.configToEdit.set(
                this._businessIntelligenceRepresentationsProvider.oldRepresentationConfigToNewRepresentationConfig(
                    this._lastUserUpdatedState.representationCode,
                    this._lastUserUpdatedState.representationConfig,
                    newRepresentationCode,
                    {
                        measures: this.availableMeasures(),
                        dimensions: this.availableDimensions()
                    }
                )
            );
        }

        this.representationSelected.set(newRepresentationCode);
        this.emitCurrentState();
    }

    onConfigChange(config: AnyBusinessIntelligenceRepresentationConfig) {
        this.configToEdit.set(config);
        this.updateLastUserUpdatedState();
        this.emitCurrentState();
    }

    updateLastUserUpdatedState() {
        this._lastUserUpdatedState = {
            representationCode: this.representationSelected(),
            representationConfig: this.configToEdit()
        };
    }

    emitCurrentState() {
        const representationConfig = this.configToEdit();
        const event: RepresentationWidgetConfigEvent = {
            isValid: this._businessIntelligenceRepresentationsProvider.isRepresentationConfigValid(
                this.representationSelected(),
                representationConfig,
                {
                    measures: this.availableMeasures(),
                    dimensions: this.availableDimensions()
                }
            ),
            biRepresentation: {
                representationCode: this.representationSelected(),
                representationConfig
            } as BusinessIntelligenceRepresentation
        };
        this.configChanged.emit(event);
    }

    private resetConfig(representationSelected: BusinessIntelligenceRepresentationCode) {
        if (!representationSelected) return;
        this.onConfigChange(this._businessIntelligenceRepresentationsProvider.getDefaultRepresentationConfig(representationSelected));
    }
}
